/**
 * The grouping-chain editor: an ordered breadcrumb of chips — outermost level first, because the order *is* the
 * nesting. Levels are removed with the chip's ✕, reordered by dragging, and added from a menu of the levels the page
 * offers. One control for every page that groups (the plan rollup's By Account tab, the pre-calculation's value sheet):
 * a page passes its levels, the control knows none of them.
 *
 * The chain is a display concern only: grouping re-partitions and never changes the total.
 */

import { useMemo, useState } from 'react';
import { Box, Button, Chip, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

/** A level the page can group by: its token in the chain and its label. */
export interface GroupByLevel {
  token: string;
  label: string;
}

export interface GroupByChainProps {
  /** The applied chain as tokens, outermost first. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Reset target — the page's preset, so "back to how it was" is one click. */
  defaultValue: string[];
  /** Every level the page offers; one already in the chain is not offered again. */
  levels: GroupByLevel[];
  /** A chain token's label; a token no offered level names (one since removed) shows as itself unless this names it. */
  labelFor?: (token: string) => string | undefined;
  /** What an empty chain means on the page. */
  flatLabel: string;
  maxLevels: number;
}

export function GroupByChain({ value, onChange, defaultValue, levels, labelFor, flatLabel, maxLevels }: GroupByChainProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const label = (token: string) => labelFor?.(token) ?? levels.find((l) => l.token === token)?.label ?? token;
  // A level cannot repeat, so what is already in the chain is not offered again.
  const available = useMemo(() => levels.filter((l) => !value.includes(l.token)), [levels, value]);

  const atCap = value.length >= maxLevels;
  const isDefault = value.length === defaultValue.length && value.every((v, i) => v === defaultValue[i]);

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  const moveTo = (from: number, to: number) => {
    if (from === to) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Grouping.Label', { defaultValue: 'Group by' })}
      </Typography>

      {value.map((token, index) => (
        <Box
          key={token}
          draggable
          onDragStart={() => setDragIndex(index)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIndex !== null) moveTo(dragIndex, index);
            setDragIndex(null);
          }}
          onDragEnd={() => setDragIndex(null)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'grab' }}
        >
          {index > 0 && (
            <Typography component="span" variant="body2" color="text.disabled">
              ›
            </Typography>
          )}
          <Chip
            size="small"
            label={label(token)}
            onDelete={() => removeAt(index)}
            sx={{ opacity: dragIndex === index ? 0.5 : 1 }}
          />
        </Box>
      ))}

      {value.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {flatLabel}
        </Typography>
      )}

      <Tooltip title={atCap ? t('Grouping.AtCap', { defaultValue: 'A grouping can have at most {{max}} levels.', max: maxLevels }) : ''}>
        {/* A disabled button swallows pointer events, so the tooltip needs a wrapper to hang on. */}
        <span>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={atCap || available.length === 0}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ textTransform: 'none' }}
          >
            {t('Grouping.AddLevel', { defaultValue: 'Add level' })}
          </Button>
        </span>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {available.map((option) => (
          <MenuItem
            key={option.token}
            onClick={() => {
              onChange([...value, option.token]);
              setAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {!isDefault && (
        <Button size="small" onClick={() => onChange([...defaultValue])} sx={{ textTransform: 'none' }}>
          {t('Grouping.Reset', { defaultValue: 'Reset' })}
        </Button>
      )}
    </Box>
  );
}
